import auth from 'api/auth';
import { socketUrl } from 'api/ws';
import { getCookie, resetTokensAndSendToLogin, setCookie } from 'helpers/cookies';
import { jwtDecode } from 'jwt-decode';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';
import { getDownloadMessage } from 'routes/Documents/Documents';
import { useAppDispatch, useAppSelector } from 'store/hook';
import fetchChatData, {
  fetchChatInfo,
  fetchUnviewedAmount,
  setMessagesToLocalStorage,
  setRejectMessages,
  switchChatMessagesStatus,
} from 'store/reducers/chatCreator';
import fetchDocumentsData, {
  deleteThisDocument,
  deleteThisFolder,
  fetchConvertedFile,
  fetchDocumentMove,
  fetchFolderMove,
  fetchDocumentUpdate,
  fetchFoldersPermissions,
  fetchNewFolder,
  fetchUpdatedRoom,
  updateDocumentUpload,
  updateSigningState,
  handleDocumentSigning,
} from 'store/reducers/documentsSocketsCreator';
import { fetchAvailableDocuments } from 'store/reducers/documentsCreator';
import { blockRoom, fetchNotificationUnviewed, fetchRoomNotifications, kickUserCheck } from 'store/reducers/roomsCreator';
import { clearReduxFunction, updateRoomsBySockets } from 'store/reducers/userDataCreator';
import { fetchGlobalUsers, fetchUsersOnSokcet } from 'store/reducers/usersGroupCreator';
import { fetchUserPermissions } from 'store/reducers/userDataCreator';
import {
  setChatMessage,
  setChatStatus,
  setCheckingMessages,
  setIsLoadedSession,
  updateChatLastMessage,
} from 'store/slices/chatsSlice';
import { setIsOpenFailedPayment, setIsOpenSuccessfulPayment } from 'store/slices/windowStateSlice';
import { 
  clearUploadingDocuments, 
  clearUploadingFolders, 
  setCanceledUploads,
  setUploadAbortController 
} from 'store/slices/dataDocumentsSlice';
import { updateAnalysisStatus, updateTranscription } from 'store/reducers/aiFunctionsCreator';
import { refresTokensHandle } from 'helpers/tokens';

export default function WorkerComponent() {
  const [isPausedWS, setIsPausedWS] = React.useState(false);
  
  const workerRef = React.useRef<SharedWorker | null>(null);
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const userEmail = searchParams.get('email') || '';
  const { userData } = useAppSelector((store) => store.userData);
  const { dataRoom } = useAppSelector((store) => store.dataRoom);
  const { messageApi } = useAppSelector((store) => store.windowState);
  const { currentFolder } = useAppSelector((store) => store.documents);
  const {
    chatStatus,
    chatMessage,
    selectedChat,
    isLoadedSession,
    checkingMessages,
    listStatus,
    viewsMessagesIds,
    roomChatLoading,
  } = useAppSelector((store) => store.chat);
      
  const locale = i18n.language || localStorage.getItem('language') || userData?.language || 'en';

  const chatCases = {
    pending: (message: any) => {},
    rejected: (message: any) => {},
    send: ({ status, timestamp, ...message }: any) => {
      const data = { ...message, status, timestamp, type: listStatus };
      const newCheckingMessages = [...checkingMessages, data];
      workerRef.current?.port.postMessage({ event: 'chat', message });
      dispatch(setChatStatus('pending'));
      dispatch(
        setChatMessage({
          id: '',
          status: 'pending',
          message: '',
          replied_to: null,
          attachment_ids: [],
        })
      );
      dispatch(setCheckingMessages(newCheckingMessages));
    },
  };

  const workerMessageListeners = React.useMemo(() => {
    return {
      'auth:get': (message: any) => {
        console.log('Session is authed', message);
      },

      new_chat: (data: any) => {
        console.log('new chat created -', data);
        dispatch(
          fetchChatInfo({ chatId: data.new_chat_id, ownerId: userData?.id! })
        );
      },

      new_global_chat: (data: any) => {
        console.log('new global chat created -', data);
        // The 'data' object does not contain the 'new_chat_id' field.
        // dispatch(fetchChatInfo({ chatId: data.new_chat_id, ownerId: userData?.id! }));
      },

      document_create: async (data: any) => {
        dispatch(
          fetchDocumentsData({ type: 'replace', websocketData: data, navigate })
        );
        console.log('Get session message:', data);
      },

      document_ready: async (data: any) => {
        dispatch(fetchConvertedFile({data, translation: { t, i18n }, getDownloadMessage}));
        console.log('Get session message:', data);
      },

      signing_update: async (data: any) => {
        dispatch(
          fetchDocumentsData({ type: 'replace', websocketData: data, navigate })
        );
        dispatch(updateSigningState(data));
        console.log('Get session message:', data);
      },

      document_signing: async (data: any) => {
        console.log('Document signing status:', data);
        dispatch(handleDocumentSigning(data));
      },

      folder_create: async (data: any) => {
        console.log('Get session message:', data);
        dispatch(fetchNewFolder(data));
      },

      document_delete: async (data: any) => {
        console.log('Document deleted:', data);
        dispatch(deleteThisDocument(data));
      },

      folder_delete: async (data: any) => {
        console.log('Folder deleted:', data);
        dispatch(deleteThisFolder(data));
      },

      document_move: async (data: any) => {
        console.log('Document moved:', data);
        dispatch(fetchDocumentMove(data));
      },

      folder_move: async (data: any) => {
        console.log('Folder moved:', data);
        dispatch(fetchFolderMove(data));
      },

      document_update: async (data: any) => {
        console.log('Document updated:', data);
        dispatch(fetchDocumentUpdate(data));
      },

      document_replace: async (data: any) => {
        console.log('Document version updated:', data);
        dispatch(fetchDocumentUpdate({ ...data, prev_version: data.prev_version }));
      },

      document_upload: async (data: any) => {
        try {
          console.log('Document upload:', data);
          dispatch(updateDocumentUpload(data));
        } catch (error) {
          console.error('Error parsing document_upload message:', error);
        }
      },

      permissions_update: async (data: any) => {
        console.log('Document updated:', data);
        dispatch(fetchFoldersPermissions(data));
      },

      notification_create: async (message: any) => {
        console.log('Notification created:', message);
        dispatch(fetchRoomNotifications({}));
        dispatch(fetchNotificationUnviewed({}));
      },

      rooms_update: async (data: any) => {
        console.log('Message:', data);
        dispatch(updateRoomsBySockets());
      },

      connect_to_server: async (data: any) => {
        console.log(data);
        const refresh_token = getCookie('refresh_token');
        const newTokens = await refresTokensHandle(refresh_token);

        const userCheckingMessages = localStorage.getItem(userData?.email!);
        const checkingMessagesCount = userCheckingMessages
          ? JSON.parse(userCheckingMessages).length
          : 0;
        workerRef?.current!.port.postMessage({
          event: 'connect',
          url: socketUrl,
          checkingMessagesCount,
          userId: userData?.id,
          email: userEmail,
          token: newTokens.access_token,
          log: 'reconnected',
        });
      },

      message_cancel: async (data: any) => {
        console.log('Rejected messages', data);
        const action = (await dispatch(
          setRejectMessages({ type: data.type })
        )) as any;
        action.payload.isShowSupport &&
          messageApi?.warning(
            <span
              dangerouslySetInnerHTML={{
                __html: t('Chats.rejectedMessages.support'),
              }}
            />,
            7
          );
      },

      message_view: (data: any) => {
        console.log('Get viewing message:', data);
        dispatch(switchChatMessagesStatus(data.message));
      },

      successful_payment: async (data: any) => {
        console.log('Payment info:', data);
        dispatch(setIsOpenSuccessfulPayment(true));
      },

      failed_payment: async (data: any) => {
        console.log('Payment info:', data);
        dispatch(setIsOpenFailedPayment(true));
      },

      chat: async (data: any) => {
        console.log('Get session message in chat:', data);
        dispatch(fetchUnviewedAmount({ data, count: 1 }));
        dispatch(fetchChatData({ data, type: 'replace' }));
        dispatch(
          updateChatLastMessage({
            chat_id: data.chat_id,
            content: data.message,
            id: data.id,
            status: 'sent',
            author: {
              id: data.id,
              name: data.user_name,
              email: data.email,
            },
            created_at: data.created_at,
            attachment_ids: data.attachment_ids,
          })
        );
      },

      message_from_ai_assistant: async (data: any) => {
        console.log('Get session message from AI:', data);
        dispatch(fetchChatData({ data, type: 'replace' }));
      },

      agreement_update: async (data: any) => {
        console.log('Agreement updated:', data);
        dispatch(fetchUpdatedRoom({ socketData: data, navigate, t }));
      },

      members_update: async (data: any) => {
        console.log('Change invite status info:', data);
        dispatch(fetchUsersOnSokcet(data.message));
        dispatch(fetchGlobalUsers());
      },

      kick_user: async (data: any) => {
        console.log('Message:', data);
        dispatch(kickUserCheck({idRoom: data.room_id, navigate}));
      },

      transcription_status_update: async (data: any) => {
        console.log('Transcription update status:', data);
        dispatch(updateTranscription({...data, t}));
      },

      report_status_update: async (data: any) => {
        console.log('Report update status:', data);
        dispatch(updateAnalysisStatus({...data, t}));
      },

      update_user_blocked: async (data: any) => {
        console.log('Message:', data);
        dispatch(blockRoom({data, navigate, t}));
      },

      update_role_permissions: async (data: any) => {
        console.log('Role permissions updated:', data);
          dispatch(fetchUsersOnSokcet(data));
          await dispatch(fetchUserPermissions({ idRoom: data.room_id }));
          const currentFolderId = currentFolder[currentFolder.length - 1]?.id || null;
          await dispatch(fetchAvailableDocuments({ 
            roomId: data.room_id, 
            folderId: currentFolderId 
          }));
          
      },

      init: (data: any) => {
        console.log('init -', data);
      },

      logout: (data: any) => {
        resetTokensAndSendToLogin();
        dispatch(clearReduxFunction({}))
        setCookie('sid', '', 1);
        
        // Очистка состояний загрузки документов
        dispatch(clearUploadingDocuments());
        dispatch(clearUploadingFolders());
        dispatch(setCanceledUploads([]));
        dispatch(setUploadAbortController(null));
      },

      disconnect: () => {
        setIsPausedWS(true);
        console.log(`The session is closed at: ${new Date().toLocaleString()}`);
      },
    };
  }, [locale, userData, workerRef]);

  React.useEffect(() => {
    if (!isPausedWS) {
      const userCheckingMessages = localStorage.getItem(userData?.email!);
      const checkingMessagesCount = userCheckingMessages
        ? JSON.parse(userCheckingMessages).length
        : 0;
      // @ts-ignore
      workerRef.current = new SharedWorker(
        new URL('../../utils/Worker.ts', import.meta.url)
      );
      const token = getCookie('athena-token');
      workerRef.current.port.start();
      workerRef.current.port.postMessage({
        event: 'connect',
        url: socketUrl,
        checkingMessagesCount,
        userId: userData?.id,
        email: userEmail,
        token,
      });
      workerRef.current.port.onmessage = (message: any) => {
        if (message.data.event === 'connect') {
          setCookie('sid', message.data.sid || '', 1);
          dispatch(setIsLoadedSession(true));
        } else {
          (
            workerMessageListeners[
              message.data?.event as keyof typeof workerMessageListeners
            ] || function () {}
          )(message.data);
        }
      };

      window.addEventListener('beforeunload', () => {
        workerRef.current?.port.postMessage({ event: 'close' });
        dispatch(setMessagesToLocalStorage());
      });
    }

    return () => {
      if (!getCookie('athena-token')) {
        workerRef.current?.port.postMessage({ event: 'logout' });
        workerRef.current?.port.postMessage({ event: 'close' });
      }
      if (isLoadedSession) workerRef.current?.port.close();
    };
  }, [isPausedWS]);

  React.useEffect(() => {
    if (dataRoom?.id && isLoadedSession) {
      workerRef?.current!.port.postMessage({
        event: 'init',
        roomId: dataRoom?.id,
      });
    }
  }, [dataRoom?.id, isLoadedSession]);

  React.useMemo(
    () =>
      document.addEventListener('click', function () {
        if (isLoadedSession) {
          setIsPausedWS(false);
        }
      }),
    [isLoadedSession, isPausedWS]
  );

  React.useEffect(() => {
    const data = {
      room_id: dataRoom?.id,
      message: chatMessage.message,
      attachment_ids: chatMessage.attachment_ids,
      chat_id: selectedChat?.id,
      created_at: chatMessage.id,
      status: chatMessage.status,
      email: userData?.email!,
      user_name: `${userData?.first_name} ${userData?.last_name}`,
      timestamp: chatMessage.id,
      replied_to: chatMessage.replied_to ? chatMessage.replied_to.id : null,
    };

    chatCases[chatStatus](data);
  }, [chatStatus]);

  React.useEffect(() => {
    if (viewsMessagesIds.length) {
      workerRef.current!.port.postMessage({
        event: 'message_view',
        message: { message_ids: viewsMessagesIds },
      });
    }
  }, [viewsMessagesIds]);

  React.useEffect(() => {
    if (!roomChatLoading) {
      workerRef.current?.port.postMessage({
        event: 'check_messages_canceling',
      });
    }
  }, [roomChatLoading]);

  // Функционал прерывания сессии при неактивной вкладке браузера (сворачивание браузера/переход на другую вкладку)
  // React.useMemo(() => document.addEventListener('visibilitychange', function(){
  //   if (document.hidden && !isPausedWS && sessionChanel.current?.readyState === 1){
  //     setIsPausedWS(true);
  //     // console.log(`Сlosed: ${new Date().toLocaleString()}`);
  //     // sessionChanel.current?.send(`Сlosed: ${new Date().toLocaleString()}`);
  //     sessionChanel.current?.close();
  //   } else if (!document.hidden && isPausedWS) {
  //     setIsPausedWS(false);
  //   }
  // }), [sessionChanel.current?.readyState]);

  // Ручное прерыване канала вебсокета для тестирования
  // const closeWS = () => {
  //   sessionChanel.current?.close();
  //   // console.log(`Сlosed: ${new Date().toLocaleString()}`);
  //   // setIsPausedWS(true);
  // }
  // return (
  //   !isPausedWS
  //     ? <button style={{position: 'absolute'}} onClick={closeWS}>{ 'Остановить соединение' }</button>
  //     : null
  // );

  return null;
}
